import {
  Anchor,
  Button,
  Checkbox,
  Flex,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip
} from "@mantine/core";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { DateTime, Duration } from "luxon";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { z } from "zod";
import { useForm } from "@mantine/form";
import { IconArrowNarrowRight, IconPlus } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type Recording,
  type RecordingInList,
  type RecordingInListWithStatus,
  TranscriptionStatusMap
} from "@/models";
import { DataTable } from "@/components/DataTable";
import { RecordingModal } from "@/components/RecordingModal";
import {
  getRecordingTypesQuery,
  toggleNotesLivingStoryInclusion,
  toggleSummaryLivingStoryInclusion,
  useApi
} from "@/api";
import { showError, showSuccess } from "@/notifications/Notifications";
import {
  canAddOrEditRecordings,
  canEditLivingStory,
  canViewRecordings
} from "@/security/logic";
import { useRecordingsWithLocalSessions } from "@/hooks/useRecordingsWithLocalSessions";

export const Route = createFileRoute("/prospects/$prospectId/recordings")({
  component: RouteComponent,
  loader: () => {
    return {
      crumb: "Recordings"
    };
  }
});

const recordingFormSchema = z.object({
  recordingTypeId: z
    .number({
      message: "Please select a recording type"
    })
    .positive("Please select a recording type"),
  recordingTitle: z
    .string()
    .min(1, "Recording title is required")
    .max(128, "Recording title must be less than 128 characters")
});

type RecordingFormValues = z.infer<typeof recordingFormSchema>;

function RouteComponent() {
  const { prospect, userClaim } = Route.useRouteContext();
  const { secureAxios } = useApi();
  const queryClient = useQueryClient();

  const prospectName = `${prospect?.firstName} ${prospect?.lastName}`;

  // if user does not have permission to view recordings, show custom empty state instead of querying
  const userHasPermissionToViewRecordings = canViewRecordings(
    userClaim,
    prospect?.communityId ?? 0
  );
  const { recordings, isLoading } = useRecordingsWithLocalSessions(
    secureAxios,
    prospect?.id ?? 0,
    userHasPermissionToViewRecordings
  );

  const hasEditPermission = canAddOrEditRecordings(
    userClaim,
    prospect?.communityId ?? 0
  );

  const hasEditLivingStoryPermission = canEditLivingStory(
    userClaim,
    prospect?.communityId ?? 0
  );

  const { data: recordingTypes = [] } = useQuery(
    getRecordingTypesQuery(secureAxios)
  );

  const toggleSummaryMutation = useMutation<
    boolean,
    Error,
    { recordingId: string; includeSummary: boolean }
  >({
    mutationFn: async ({ recordingId, includeSummary }) => {
      return await toggleSummaryLivingStoryInclusion(
        secureAxios,
        recordingId,
        includeSummary
      );
    },
    onSuccess: (updatedValue, variables) => {
      queryClient.setQueryData<RecordingInList[]>(
        ["recordings", "prospect", prospect?.id],
        (oldData) => {
          if (!oldData) {
            return oldData;
          }
          return oldData.map((recording) =>
            recording.id === variables.recordingId
              ? { ...recording, includeSummaryInLivingStory: updatedValue }
              : recording
          );
        }
      );
      showSuccess(
        "Living story summary inclusion updated successfully",
        "Saved"
      );
    },
    onError: (error) => {
      showError(
        "Failed to update living story summary inclusion. Please try again."
      );
      console.error("Failed to update living story summary inclusion:", error);
    }
  });

  const toggleNotesMutation = useMutation<
    boolean,
    Error,
    {
      recordingId: string;
      includeNotesInLivingStory: boolean;
    }
  >({
    mutationFn: async ({ recordingId, includeNotesInLivingStory }) => {
      return await toggleNotesLivingStoryInclusion(
        secureAxios,
        recordingId,
        includeNotesInLivingStory
      );
    },
    onSuccess: (updatedValue, variables) => {
      // Update the recording session in the query cache with the confirmed saved values
      queryClient.setQueryData<RecordingInList[]>(
        ["recordings", "prospect", prospect?.id],
        (oldData) => {
          if (!oldData) {
            return oldData;
          }
          return oldData.map((recording) =>
            recording.id === variables.recordingId
              ? {
                  ...recording,
                  includeNotesInLivingStory: updatedValue
                }
              : recording
          );
        }
      );
      // Also update it in the individual recording session cache with the confirmed saved values.
      queryClient.setQueryData<Recording | undefined>(
        ["recording", "session", variables.recordingId],
        (old) =>
          old
            ? {
                ...old,
                includeNotesInLivingStory: updatedValue
              }
            : old
      );
      showSuccess("Living story notes inclusion updated successfully", "Saved");
    },
    onError: (error) => {
      showError(
        "Failed to update living story notes inclusion. Please try again."
      );
      console.error("Failed to update living story notes inclusion:", error);
    }
  });

  const [showConsentModal, setShowConsentModal] = useState(false);
  const [hasGivenConsent, setHasGivenConsent] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isTitleEditable, setIsTitleEditable] = useState(false);

  const form = useForm<RecordingFormValues>({
    mode: "uncontrolled",
    initialValues: {
      recordingTypeId: 0,
      recordingTitle: ""
    },
    validate: (values: unknown) => {
      try {
        recordingFormSchema.parse(values);
        return {};
      } catch (error) {
        if (error instanceof z.ZodError) {
          return error.issues.reduce(
            (acc, issue) => {
              acc[issue.path[0] as keyof RecordingFormValues] = issue.message;
              return acc;
            },
            {} as Record<keyof RecordingFormValues, string>
          );
        }
        return {};
      }
    }
  });
  const openCreateModal = () => {
    setShowCreateModal(true);
    form.reset();
    setIsTitleEditable(false);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    form.reset();
    setIsTitleEditable(false);
  };

  const handleFormSubmit = () => {
    setShowCreateModal(false);
    setShowConsentModal(true);
    setHasGivenConsent(false);
  };

  const closeConsentModal = () => {
    setShowConsentModal(false);
    setHasGivenConsent(false);
  };

  const handleRecordingStopped = (sessionData: {
    id: string;
    startedDate: string;
    durationMs: number;
    recordingTitle: string;
    recordingTypeId: number;
    recordingTypeName: string;
  }) => {
    setShowConsentModal(false);
    form.reset();
    setIsTitleEditable(false);
    setHasGivenConsent(false);

    // No need to optimistically update the query cache here.
    // The useRecordingsWithLocalSessions hook polls IndexedDB and will
    // automatically pick up the new LocalRecordingSession within ~2 seconds.
    // This avoids the stale-data problem where React Query refetches would
    // replace the optimistic entry with server data that doesn't include it yet.
    void sessionData;
  };

  const handleRecordingDeleted = () => {
    setShowConsentModal(false);
    form.reset();
    setIsTitleEditable(false);
    setHasGivenConsent(false);
  };

  const generateRecordingTitle = (name: string, recordingTypeName: string) => {
    const currentDate = DateTime.now().toLocaleString({
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });

    if (name && recordingTypeName) {
      return `${name} - ${recordingTypeName} - ${currentDate}`;
    }
    return "";
  };

  const handleRecordingTypeChange = (value: string | null) => {
    if (value) {
      const recordingTypeId = parseInt(value, 10);
      const selectedType = recordingTypes.find(
        (rt) => rt.id === recordingTypeId
      );

      if (selectedType) {
        form.setFieldValue("recordingTypeId", recordingTypeId);
        if (!isTitleEditable) {
          const newTitle = generateRecordingTitle(
            prospectName,
            selectedType.name
          );
          if (newTitle) {
            form.setFieldValue("recordingTitle", newTitle);
          }
        }
      }
    }
  };

  const handleEditTitleClick = () => {
    setIsTitleEditable(true);
  };

  const columnHelper = createColumnHelper<RecordingInListWithStatus>();

  return (
    <>
      <Flex justify={"space-between"} align="center" mb="md">
        <Title order={5}>Recordings</Title>
        <Tooltip
          disabled={hasEditPermission}
          label={
            hasEditPermission
              ? undefined
              : "You do not have access to create a recording."
          }
        >
          <Button
            disabled={!hasEditPermission}
            onClick={openCreateModal}
            leftSection={<IconPlus size={18} />}
            color="blue"
          >
            Create a New Recording
          </Button>
        </Tooltip>
      </Flex>

      <Modal
        opened={showCreateModal}
        onClose={closeCreateModal}
        closeOnClickOutside={false}
        closeOnEscape={false}
        title="Create New Recording"
        size="lg"
        centered
      >
        <form onSubmit={form.onSubmit(handleFormSubmit)}>
          <Stack gap="md">
            <TextInput
              label="PROSPECT/RESIDENT NAME"
              value={prospectName}
              readOnly
            />

            <Select
              label="RECORDING TYPE (REQUIRED)"
              placeholder="Select recording type"
              data={recordingTypes.map((type) => ({
                value: type.id.toString(),
                label: type.name
              }))}
              {...form.getInputProps("recordingTypeId")}
              onChange={handleRecordingTypeChange}
              required
            />
            <div>
              <Text size="sm" fw={500} mb={4}>
                RECORDING TITLE (REQUIRED)
              </Text>
              <Flex gap="xs" align="flex-start">
                <TextInput
                  placeholder="Recording title will be auto-generated"
                  {...form.getInputProps("recordingTitle")}
                  readOnly={!isTitleEditable}
                  style={{ flex: 1 }}
                  required
                />
                <Button
                  variant="light"
                  onClick={handleEditTitleClick}
                  disabled={isTitleEditable}
                >
                  Edit Recording Title
                </Button>
              </Flex>
            </div>

            <Group justify="space-between" mt="lg">
              <Button variant="default" onClick={closeCreateModal}>
                Cancel Recording
              </Button>
              <Button
                type="submit"
                w={150}
                rightSection={<IconArrowNarrowRight size={18} />}
              >
                Next
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={showConsentModal}
        onClose={closeConsentModal}
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={false}
        title="Recording Notice"
        size="lg"
        centered
      >
        <Stack gap="lg">
          <Text size="sm">
            Before starting a recording, you must inform all participants that
            the session will be recorded.
          </Text>
          <Text size="sm">
            Do not begin until you have received their explicit permission.
          </Text>
          <Text size="sm" fw={500}>
            Recording without consent may violate privacy laws and
            organizational policy.
          </Text>

          <Checkbox
            checked={hasGivenConsent}
            onChange={(e) => setHasGivenConsent(e.currentTarget.checked)}
            label="I understand and have received explicit permission from all the participants."
            c="red.9"
          />

          <Group justify="space-between" mt="lg">
            <Button variant="default" onClick={closeConsentModal}>
              Cancel Recording
            </Button>
            <RecordingModal
              hasConsent={hasGivenConsent}
              onRecordingStopped={handleRecordingStopped}
              onRecordingDeleted={handleRecordingDeleted}
              prospectName={prospectName}
              recordingTypeId={form.values.recordingTypeId}
              recordingTypeName={
                recordingTypes.find(
                  (rt) => rt.id === form.values.recordingTypeId
                )?.name ?? ""
              }
              recordingTitle={form.values.recordingTitle}
              prospectId={prospect?.id}
            />
          </Group>
        </Stack>
      </Modal>

      <DataTable
        columns={[
          columnHelper.accessor("recordingTitle", {
            header: "Recording Name",
            cell: ({ getValue, row }) => {
              let value = getValue<string>();
              if (!value || value.trim() === "") {
                value = "(Untitled Recording)";
              }
              if (!row.original.existsOnServer) {
                // don't be a link if they can't click into it yet. (This is where we'll put resume-or-cancel functionality if we build it)
                return <Text size="sm">{value}</Text>;
              }
              return (
                <Anchor
                  component={Link}
                  to={`/prospects/${prospect?.id}/recordings/${row.original.id}`}
                >
                  {value}
                </Anchor>
              );
            }
          }),
          columnHelper.accessor("durationMs", {
            header: "Duration",
            cell: ({ getValue }) => {
              // TODO: format based on length
              return Duration.fromObject({
                milliseconds: getValue<number>()
              }).toFormat("hh:mm:ss");
            }
          }),
          columnHelper.accessor("startedDate", {
            header: "Date Created",
            cell: ({ getValue }) => {
              return (
                <span className="whitespace-nowrap">
                  {DateTime.fromISO(getValue<string>()).toFormat("yyyy-MM-dd")}
                </span>
              );
            }
          }),
          columnHelper.accessor("recordingType", {
            header: "Recording Type",
            cell: ({ getValue }) => getValue()?.name ?? ""
          }),
          columnHelper.accessor("computedStatus", {
            header: "Sync Status",
            cell: ({ getValue }) => {
              const status = getValue();
              const isError = status === "Error Transcribing";
              return (
                <Text size="sm" c={isError ? "red" : undefined}>
                  {status}
                </Text>
              );
            }
          }),
          columnHelper.accessor("includeSummaryInLivingStory", {
            header: "Living Story",
            cell: ({ getValue, row }) => (
              <Stack gap={8}>
                <Checkbox
                  size="sm"
                  styles={{ label: { paddingInlineStart: "0.4rem" } }}
                  label="Summary"
                  checked={getValue<boolean>()}
                  disabled={
                    row.original.transcriptionStatus !==
                      TranscriptionStatusMap.Succeeded ||
                    toggleSummaryMutation.isPending ||
                    !hasEditLivingStoryPermission
                  }
                  onChange={(e) => {
                    toggleSummaryMutation.mutate({
                      recordingId: row.original.id,
                      includeSummary: e.currentTarget.checked
                    });
                  }}
                />
                {row.original.hasNotes && (
                  <Checkbox
                    size="sm"
                    styles={{ label: { paddingInlineStart: "0.4rem" } }}
                    label="Notes"
                    checked={row.original.includeNotesInLivingStory}
                    disabled={
                      toggleNotesMutation.isPending ||
                      !hasEditLivingStoryPermission
                    }
                    onChange={(e) => {
                      toggleNotesMutation.mutate({
                        recordingId: row.original.id,
                        includeNotesInLivingStory: e.currentTarget.checked
                      });
                    }}
                  />
                )}
              </Stack>
            )
          })
        ]}
        data={recordings}
        isLoading={userHasPermissionToViewRecordings && isLoading}
        emptyText={
          userHasPermissionToViewRecordings
            ? "No recordings."
            : "You don't have access to this prospect's recordings. Contact your administrator to request permission."
        }
      />

      {/* Outlet renders the recording detail modal when a recording is selected */}
      <Outlet />
    </>
  );
}
